/**
 * OnboardingChecklist.js
 *
 * Validations added:
 * 1. Template modal: name required, min 2, max 150 chars
 * 2. Task inline form: title required min 2/max 200, due_days 1-365 integer
 * 3. window.confirm in deleteTemplate → ConfirmModal
 *
 * MyTasks and task status updates: no forms, unchanged.
 */
import React, { useState, useEffect } from 'react';
import {
    Card, Row, Col, Button, Badge, ProgressBar,
    Modal, Form, Spinner, Table, Tabs, Tab,
} from 'react-bootstrap';
import {
    CheckCircle, Circle, Clock, Plus, Edit2, Trash2,
    ClipboardList,
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';

// ── Shared Confirm Modal ──────────────────────────────────────────────────
const ConfirmModal = ({ show, title, message, onConfirm, onCancel, loading }) => (
    <Modal show={show} onHide={onCancel} centered size="sm">
        <Modal.Header closeButton className="border-0">
            <Modal.Title className="fw-bold fs-6">{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-2">
            <p className="text-muted small mb-0">{message}</p>
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-center gap-2">
            <Button variant="light" onClick={onCancel} disabled={loading}>Cancel</Button>
            <Button variant="danger" onClick={onConfirm} disabled={loading}>
                {loading ? <Spinner size="sm"/> : 'Delete'}
            </Button>
        </Modal.Footer>
    </Modal>
);

// ── Status dot ────────────────────────────────────────────────────────────
const StatusIcon = ({ status }) => {
    if (status==='completed')  return <CheckCircle size={18} className="text-success flex-shrink-0"/>;
    if (status==='in_progress')return <Clock        size={18} className="text-warning flex-shrink-0"/>;
    return <Circle size={18} className="text-muted flex-shrink-0"/>;
};

// ════════════════════════════════════════════════════════════════════════════
// MY TASKS (Employee view) — unchanged
// ════════════════════════════════════════════════════════════════════════════
const MyTasks = () => {
    const [tasks,   setTasks]   = useState([]);
    const [loading, setLoading] = useState(true);
    const fetchTasks = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/onboarding/my-tasks');
            setTasks(res.data.data || []);
        } catch { toast.error('Failed to load tasks'); }
        finally { setLoading(false); }
    };
    useEffect(() => { fetchTasks(); }, []);
    const updateStatus = async (taskId, status) => {
        try {
            await axiosInstance.patch(`/onboarding/my-tasks/${taskId}/status`, { status });
            setTasks(prev => prev.map(t => t.task_id===taskId ? {...t, status} : t));
            if (status==='completed') toast.success('Task completed!');
        } catch { toast.error('Failed to update task'); }
    };
    const done  = tasks.filter(t => t.status==='completed').length;
    const total = tasks.length;
    const pct   = total ? Math.round((done/total)*100) : 0;
    if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>;
    return (
        <div>
            {total > 0 && (
                <Card className="border-0 shadow-sm mb-4 p-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="fw-bold">Onboarding Progress</span>
                        <span className="text-muted small">{done} / {total} tasks</span>
                    </div>
                    <ProgressBar now={pct} label={`${pct}%`}
                        variant={pct===100?'success':'primary'} style={{height:10}}/>
                    {pct===100 && (
                        <p className="text-success small mt-2 mb-0">
                            <CheckCircle size={14} className="me-1"/>All onboarding tasks complete!
                        </p>
                    )}
                </Card>
            )}
            {tasks.length===0 ? (
                <Card className="border-0 shadow-sm p-5 text-center">
                    <ClipboardList size={40} className="text-muted mx-auto mb-3 d-block"/>
                    <h6 className="fw-bold text-muted">No onboarding tasks assigned yet</h6>
                    <p className="text-muted small">Your HR team will assign onboarding tasks once they've set up a checklist.</p>
                </Card>
            ) : (
                tasks.map(task => (
                    <Card key={task.task_id||task.id} className="border-0 shadow-sm mb-2">
                        <Card.Body className="p-3">
                            <div className="d-flex align-items-start gap-3">
                                <StatusIcon status={task.status}/>
                                <div className="flex-grow-1">
                                    <div className="fw-medium" style={{
                                        textDecoration: task.status==='completed' ? 'line-through' : 'none',
                                        color: task.status==='completed' ? '#94a3b8' : 'inherit',
                                    }}>{task.title}</div>
                                    {task.description && <div className="text-muted small mt-1">{task.description}</div>}
                                    <div className="d-flex gap-2 mt-2 flex-wrap">
                                        <Badge bg="light" text="dark" className="small">
                                            Due within {task.due_days} days
                                        </Badge>
                                        <Badge bg="light" text="dark" className="small text-capitalize">
                                            {task.assigned_to}
                                        </Badge>
                                    </div>
                                </div>
                                <div className="d-flex gap-1 flex-shrink-0">
                                    {task.status!=='in_progress' && task.status!=='completed' && (
                                        <Button size="sm" variant="outline-warning"
                                            onClick={()=>updateStatus(task.task_id||task.id,'in_progress')}>
                                            Start
                                        </Button>
                                    )}
                                    {task.status!=='completed' && (
                                        <Button size="sm" variant="outline-success"
                                            onClick={()=>updateStatus(task.task_id||task.id,'completed')}>
                                            Done
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                ))
            )}
        </div>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// TEMPLATE MANAGER (Admin/HR view) — VALIDATIONS ADDED
// ════════════════════════════════════════════════════════════════════════════
const TemplateManager = () => {
    const [templates, setTemplates] = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [modal,     setModal]     = useState({ show:false, editing:null });
    const [taskModal, setTaskModal] = useState({ show:false, templateId:null, tasks:[], editing:null });
    const [form,      setForm]      = useState({ title:'', description:'' });
    const [formError, setFormError] = useState('');
    const [formAttempted, setFormAttempted] = useState(false);
    const [taskForm,  setTaskForm]  = useState({ title:'', description:'', due_days:7, assigned_to:'hr', sort_order:0 });
    const [taskErrors,setTaskErrors]= useState({});
    const [taskAttempted, setTaskAttempted] = useState(false);
    const [saving,    setSaving]    = useState(false);
    const [confirm,   setConfirm]   = useState({ show:false, id:null, name:'', loading:false });

    const fetchTemplates = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/onboarding/templates');
            setTemplates(res.data.data || []);
        } catch { toast.error('Failed to load templates'); }
        finally { setLoading(false); }
    };
    useEffect(() => { fetchTemplates(); }, []);

    const openTemplate = (t=null) => {
        setModal({ show:true, editing:t });
        setForm({ title:t?.title||'', description:t?.description||'' });
        setFormError('');
        setFormAttempted(false);
    };
    const closeTemplate = () => {
        setModal({ show:false, editing:null });
        setFormError('');
        setFormAttempted(false);
    };

    const handleFormName = (val) => {
        setForm(p=>({...p,title:val}));
        if (formAttempted) {
            const n = val?.trim();
            if (!n) setFormError('Template name is required');
            else if (n.length<2) setFormError('Name must be at least 2 characters');
            else if (n.length>150) setFormError('Name cannot exceed 150 characters');
            else setFormError('');
        }
    };

    const saveTemplate = async () => {
        setFormAttempted(true);
        const n = form.title?.trim();
        if (!n)             { setFormError('Template name is required'); return; }
        if (n.length<2)     { setFormError('Name must be at least 2 characters'); return; }
        if (n.length>150)   { setFormError('Name cannot exceed 150 characters'); return; }
        setSaving(true);
        try {
            if (modal.editing) {
                await axiosInstance.put(`/onboarding/templates/${modal.editing.id}`,{...form,is_active:1});
                toast.success('Template updated');
            } else {
                await axiosInstance.post('/onboarding/templates', form);
                toast.success('Template created');
            }
            closeTemplate(); fetchTemplates();
        } catch (err) { toast.error(err.response?.data?.message||'Save failed'); }
        finally { setSaving(false); }
    };

    const handleDeleteConfirm = async () => {
        setConfirm(p=>({...p,loading:true}));
        try {
            await axiosInstance.delete(`/onboarding/templates/${confirm.id}`);
            toast.success('Deleted'); fetchTemplates();
        } catch { toast.error('Delete failed — template may be in use'); }
        finally { setConfirm({show:false,id:null,name:'',loading:false}); }
    };

    const openTasks = async (template) => {
        try {
            const res = await axiosInstance.get(`/onboarding/templates/${template.id}/tasks`);
            setTaskModal({ show:true, templateId:template.id, tasks:res.data.data||[], editing:null });
            setTaskForm({ title:'', description:'', due_days:7, assigned_to:'hr', sort_order:0 });
            setTaskErrors({});
            setTaskAttempted(false);
        } catch { toast.error('Failed to load tasks'); }
    };

    // ── Task validation ───────────────────────────────────────────────────
    const validateTask = (tf) => {
        const e = {};
        const t = tf.title?.trim();
        if (!t)              e.title = 'Task title is required';
        else if (t.length<2) e.title = 'Title must be at least 2 characters';
        else if (t.length>200) e.title = 'Title cannot exceed 200 characters';
        const dd = parseInt(tf.due_days);
        if (isNaN(dd) || dd<1)   e.due_days = 'Due days must be at least 1';
        else if (dd>365)           e.due_days = 'Due days cannot exceed 365';
        return e;
    };

    const setTaskField = (k,v) => {
        setTaskForm(p=>({...p,[k]:v}));
        if (taskAttempted) setTaskErrors(prev=>{ const n={...prev}; delete n[k]; return n; });
    };

    const saveTask = async () => {
        setTaskAttempted(true);
        const errs = validateTask(taskForm);
        if (Object.keys(errs).length>0) { setTaskErrors(errs); return; }
        setSaving(true);
        try {
            if (taskModal.editing) {
                await axiosInstance.put(`/onboarding/templates/${taskModal.templateId}/tasks/${taskModal.editing.id}`, taskForm);
            } else {
                await axiosInstance.post(`/onboarding/templates/${taskModal.templateId}/tasks`, taskForm);
            }
            const res = await axiosInstance.get(`/onboarding/templates/${taskModal.templateId}/tasks`);
            setTaskModal(p => ({...p, tasks:res.data.data||[], editing:null}));
            setTaskForm({ title:'', description:'', due_days:7, assigned_to:'hr', sort_order:0 });
            setTaskErrors({});
            setTaskAttempted(false);
            toast.success(taskModal.editing ? 'Task updated' : 'Task added');
        } catch { toast.error('Save failed'); }
        finally { setSaving(false); }
    };

    const deleteTask = async (taskId) => {
        try {
            await axiosInstance.delete(`/onboarding/templates/${taskModal.templateId}/tasks/${taskId}`);
            setTaskModal(p=>({...p, tasks:p.tasks.filter(t=>t.id!==taskId)}));
            toast.success('Task deleted');
        } catch { toast.error('Delete failed'); }
    };

    if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>;

    return (
        <div>
            <div className="d-flex justify-content-end mb-3">
                <Button variant="primary" className="d-flex align-items-center gap-2"
                    onClick={() => openTemplate()}>
                    <Plus size={16}/>New Template
                </Button>
            </div>

            {templates.length===0 ? (
                <Card className="border-0 shadow-sm p-5 text-center">
                    <ClipboardList size={40} className="text-muted mx-auto mb-2 d-block"/>
                    <h6 className="fw-bold text-muted">No onboarding templates yet</h6>
                    <p className="text-muted small mb-3">Create a template to define the tasks new employees must complete.</p>
                    <Button variant="primary" size="sm" onClick={()=>openTemplate()}>Create First Template</Button>
                </Card>
            ) : (
                <Row className="g-3">
                    {templates.map(t => (
                        <Col key={t.id} xs={12} md={6} lg={4}>
                            <Card className="border-0 shadow-sm h-100">
                                <Card.Body className="p-3">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <h6 className="fw-bold mb-0">{t.title}</h6>
                                        <Badge bg={t.is_active?'success':'secondary'}>
                                            {t.is_active?'Active':'Inactive'}
                                        </Badge>
                                    </div>
                                    {t.description && <p className="text-muted small mb-2">{t.description}</p>}
                                    <p className="text-muted small">
                                        <ClipboardList size={13} className="me-1"/>{t.task_count||0} tasks
                                    </p>
                                    <div className="d-flex gap-2 mt-3">
                                        <Button size="sm" variant="outline-primary" className="flex-grow-1"
                                            onClick={()=>openTasks(t)}>
                                            <ClipboardList size={13} className="me-1"/>Manage Tasks
                                        </Button>
                                        <Button size="sm" variant="outline-secondary"
                                            onClick={()=>openTemplate(t)}><Edit2 size={13}/></Button>
                                        <Button size="sm" variant="outline-danger"
                                            onClick={()=>setConfirm({show:true,id:t.id,name:t.title,loading:false})}>
                                            <Trash2 size={13}/>
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            {/* Template Modal */}
            <Modal show={modal.show} onHide={closeTemplate} centered>
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="fw-bold">
                        {modal.editing ? 'Edit Template' : 'New Onboarding Template'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Template Name *</Form.Label>
                        <Form.Control
                            placeholder="e.g. Software Engineer Onboarding"
                            value={form.title} maxLength={150}
                            isInvalid={formAttempted && !!formError}
                            onChange={e => handleFormName(e.target.value)}/>
                        <Form.Control.Feedback type="invalid">{formError}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label className="small fw-bold">Description</Form.Label>
                        <Form.Control as="textarea" rows={2}
                            placeholder="Brief description of this onboarding flow"
                            value={form.description} maxLength={500}
                            onChange={e=>setForm(p=>({...p,description:e.target.value}))}/>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="light" onClick={closeTemplate}>Cancel</Button>
                    <Button variant="primary" onClick={saveTemplate} disabled={saving}>
                        {saving ? <Spinner size="sm"/> : modal.editing ? 'Save Changes' : 'Create Template'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Task Manager Modal */}
            <Modal show={taskModal.show}
                onHide={() => setTaskModal(p=>({...p,show:false}))}
                centered size="lg">
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="fw-bold">Manage Tasks</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Table hover responsive className="small mb-4 align-middle">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{fontSize:11}}>
                                <th>Task</th><th>Due</th><th>Assigned To</th>
                                <th className="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {taskModal.tasks.length>0 ? taskModal.tasks.map(task => (
                                <tr key={task.id}>
                                    <td className="fw-medium">{task.title}</td>
                                    <td>Day {task.due_days}</td>
                                    <td className="text-capitalize">{task.assigned_to}</td>
                                    <td className="text-end">
                                        <Button size="sm" variant="link" className="text-muted p-1"
                                            onClick={() => {
                                                setTaskModal(p=>({...p,editing:task}));
                                                setTaskForm({
                                                    title:task.title, description:task.description||'',
                                                    due_days:task.due_days, assigned_to:task.assigned_to,
                                                    sort_order:task.sort_order,
                                                });
                                                setTaskErrors({});
                                                setTaskAttempted(false);
                                            }}>
                                            <Edit2 size={13}/>
                                        </Button>
                                        <Button size="sm" variant="link" className="text-danger p-1"
                                            onClick={() => deleteTask(task.id)}>
                                            <Trash2 size={13}/>
                                        </Button>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={4} className="text-center py-3 text-muted">
                                        No tasks yet. Add one below.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    <h6 className="fw-bold mb-3">{taskModal.editing ? 'Edit Task' : 'Add Task'}</h6>
                    <Row className="g-2">
                        <Col xs={12}>
                            <Form.Control size="sm"
                                placeholder="Task title *"
                                value={taskForm.title} maxLength={200}
                                isInvalid={taskAttempted && !!taskErrors.title}
                                onChange={e=>setTaskField('title',e.target.value)}/>
                            <Form.Control.Feedback type="invalid">{taskErrors.title}</Form.Control.Feedback>
                        </Col>
                        <Col xs={12}>
                            <Form.Control size="sm" as="textarea" rows={2}
                                placeholder="Description (optional)"
                                value={taskForm.description} maxLength={500}
                                onChange={e=>setTaskField('description',e.target.value)}/>
                        </Col>
                        <Col xs={6} sm={3}>
                            <Form.Label className="small">Due (days) *</Form.Label>
                            <Form.Control size="sm" type="number" min={1} max={365}
                                value={taskForm.due_days}
                                isInvalid={taskAttempted && !!taskErrors.due_days}
                                onChange={e=>setTaskField('due_days',parseInt(e.target.value)||1)}/>
                            <Form.Control.Feedback type="invalid">{taskErrors.due_days}</Form.Control.Feedback>
                        </Col>
                        <Col xs={6} sm={3}>
                            <Form.Label className="small">Assigned To</Form.Label>
                            <Form.Select size="sm" value={taskForm.assigned_to}
                                onChange={e=>setTaskField('assigned_to',e.target.value)}>
                                <option value="employee">Employee</option>
                                <option value="hr">HR</option>
                                <option value="admin">Admin</option>
                                <option value="it">IT</option>
                            </Form.Select>
                        </Col>
                        <Col xs={12} sm={6} className="d-flex align-items-end gap-2">
                            {taskModal.editing && (
                                <Button size="sm" variant="light"
                                    onClick={() => {
                                        setTaskModal(p=>({...p,editing:null}));
                                        setTaskForm({title:'',description:'',due_days:7,assigned_to:'hr',sort_order:0});
                                        setTaskErrors({});
                                        setTaskAttempted(false);
                                    }}>Cancel</Button>
                            )}
                            <Button size="sm" variant="primary" className="flex-grow-1"
                                onClick={saveTask} disabled={saving}>
                                {saving ? <Spinner size="sm"/> : taskModal.editing ? 'Update Task' : 'Add Task'}
                            </Button>
                        </Col>
                    </Row>
                </Modal.Body>
            </Modal>

            {/* Delete Confirm */}
            <ConfirmModal
                show={confirm.show}
                title="Delete Template"
                message={`Delete "${confirm.name}"? All tasks in this template will be removed.`}
                onConfirm={handleDeleteConfirm}
                onCancel={() => setConfirm({show:false,id:null,name:'',loading:false})}
                loading={confirm.loading}/>
        </div>
    );
};

// ════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ════════════════════════════════════════════════════════════════════════════
const OnboardingChecklist = () => {
    const { user } = useAuth();
    const isAdmin  = ['super-admin','admin','hr-manager'].includes(user?.role);
    return (
        <div className="p-2 p-md-4">
            <div className="mb-4">
                <h4 className="fw-bold mb-1">
                    <ClipboardList size={22} className="me-2 text-primary"/>Onboarding
                </h4>
                <p className="text-muted small mb-0">
                    {isAdmin
                        ? 'Manage onboarding templates and track employee progress.'
                        : 'Track your onboarding checklist and get up to speed.'}
                </p>
            </div>
            {isAdmin ? (
                <Tabs defaultActiveKey="templates" className="mb-4">
                    <Tab eventKey="templates"
                        title={<><ClipboardList size={14} className="me-1"/>Templates</>}>
                        <TemplateManager/>
                    </Tab>
                    <Tab eventKey="mytasks"
                        title={<><CheckCircle size={14} className="me-1"/>My Tasks</>}>
                        <MyTasks/>
                    </Tab>
                </Tabs>
            ) : (
                <MyTasks/>
            )}
        </div>
    );
};

export default OnboardingChecklist;
